import AddBoxSharpIcon from '@mui/icons-material/AddBoxSharp';
import DisabledByDefaultSharpIcon from '@mui/icons-material/DisabledByDefaultSharp';
import { ButtonBase, Stack, Typography } from '@mui/material';
import {
  QuestionnaireLanguages,
  i18nEffectSubCategory,
} from 'features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import { BasicActionDialog } from 'features/ui/base-dialogs';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  subCategories: i18nEffectSubCategory[];

  handleClose(): void;

  handleProceed(): void;

  handleEffectSubCategoryNameChange(subCategoryIndex: number, lang: QuestionnaireLanguages): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleEffectSelfAssessmentQuestionChange(
    subCategoryIndex: number,
    questionIndex: number,
    lang: QuestionnaireLanguages
  ): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleEffectExternalAssessmentQuestionChange(
    subCategoryIndex: number,
    questionIndex: number,
    lang: QuestionnaireLanguages
  ): (event: React.ChangeEvent<HTMLInputElement>) => void;

  removeQuestionFromEffectQuestionSubCategory(subCategoryIndex: number, questionIndex: number): () => void;

  removeEffectSubCategory(subCategoryIndex: number): () => void;

  addNewQuestionToEffectQuestionSubCategory(subCategoryIndex: number): () => void;

  addNewEffectSubCategory(): void;
}

const AddNewEffectQuestionsDialog: React.FC<Props> = ({
  open,
  subCategories,
  handleClose,
  handleProceed,
  handleEffectSubCategoryNameChange,
  handleEffectSelfAssessmentQuestionChange,
  handleEffectExternalAssessmentQuestionChange,
  removeQuestionFromEffectQuestionSubCategory,
  removeEffectSubCategory,
  addNewQuestionToEffectQuestionSubCategory,
  addNewEffectSubCategory,
}) => {
  const { classes } = useStyles();

  const isSubCategoryEmpty = () => {
    if (
      subCategories.some((category) => category.name.de.trim() === '' || category.name.en.trim() === '') ||
      subCategories.some(
        (category) =>
          category.questions.some((question) => question.selfAssessment.de.trim() === '') ||
          category.questions.some((question) => question.selfAssessment.en.trim() === '') ||
          category.questions.some((question) => question.externalAssessment.de.trim() === '') ||
          category.questions.some((question) => question.externalAssessment.en.trim() === '')
      )
    ) {
      return true;
    }

    return false;
  };

  return (
    <BasicActionDialog
      open={open}
      maxWidth={'md'}
      dialogTitle={'Effektive Führung hinzufügen'}
      proceedButtonText={'Hinzufügen'}
      cancelButtonText={'Abbrechen'}
      handleClose={handleClose}
      handleProceed={handleProceed}
      proceedButtonDisabled={isSubCategoryEmpty()}
    >
      <Stack gap={2}>
        {subCategories.map((subCategory, subCategoryIdx) => (
          <Stack key={subCategoryIdx} className={classes.container} gap={2}>
            <Stack className={classes.container} direction={'row'} alignContent={'center'} gap={2}>
              <PaiTextField
                value={subCategory.name.de}
                onChange={handleEffectSubCategoryNameChange(subCategoryIdx, QuestionnaireLanguages.DE)}
                label={'Unterkategorie (deutsch)'}
                placeholder={'Name der Unterkategorie eingeben...'}
              />
              <PaiTextField
                value={subCategory.name.en}
                onChange={handleEffectSubCategoryNameChange(subCategoryIdx, QuestionnaireLanguages.EN)}
                label={'Unterkategorie (englisch)'}
                placeholder={'Name der Unterkategorie eingeben...'}
              />
            </Stack>
            {subCategory.questions.map((question, questionIdx) => (
              <Stack key={questionIdx} className={classes.container} gap={2}>
                <Stack direction={'row'} alignContent={'center'} gap={2}>
                  <PaiTextField
                    value={question.selfAssessment.de}
                    placeholder={'Fragetext zur Selbstbewertung...'}
                    label={'Selbstbewertung (deutsch)'}
                    onChange={handleEffectSelfAssessmentQuestionChange(subCategoryIdx, questionIdx, QuestionnaireLanguages.DE)}
                  />
                  <PaiTextField
                    value={question.selfAssessment.en}
                    placeholder={'Fragetext zur Selbstbewertung...'}
                    label={'Selbstbewertung (englisch)'}
                    onChange={handleEffectSelfAssessmentQuestionChange(subCategoryIdx, questionIdx, QuestionnaireLanguages.EN)}
                  />
                </Stack>
                <Stack direction={'row'} alignContent={'center'} gap={2}>
                  <PaiTextField
                    value={question.externalAssessment.de}
                    placeholder={'Fragetext zur Fremdbewertung...'}
                    label={'Fremdbewertung (deutsch)'}
                    onChange={handleEffectExternalAssessmentQuestionChange(subCategoryIdx, questionIdx, QuestionnaireLanguages.DE)}
                  />
                  <PaiTextField
                    value={question.externalAssessment.en}
                    placeholder={'Fragetext zur Fremdbewertung...'}
                    label={'Fremdbewertung (englisch)'}
                    onChange={handleEffectExternalAssessmentQuestionChange(subCategoryIdx, questionIdx, QuestionnaireLanguages.EN)}
                  />
                </Stack>
                {subCategory.questions.length > 1 ? (
                  <Stack direction={'row'} justifyContent={'flex-end'}>
                    <ButtonBase className={classes.deleteButton} onClick={removeQuestionFromEffectQuestionSubCategory(subCategoryIdx, questionIdx)}>
                      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                        <DisabledByDefaultSharpIcon className={classes.icon} />
                        <Typography className={classes.text}>Frage entfernen</Typography>
                      </Stack>
                    </ButtonBase>
                  </Stack>
                ) : null}
              </Stack>
            ))}
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <ButtonBase className={classes.addButton} onClick={addNewQuestionToEffectQuestionSubCategory(subCategoryIdx)}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                  <AddBoxSharpIcon className={classes.icon} />
                  <Typography className={classes.text}>weitere Frage</Typography>
                </Stack>
              </ButtonBase>
              {subCategories.length > 1 ? (
                <ButtonBase className={classes.deleteButton} onClick={removeEffectSubCategory(subCategoryIdx)}>
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                    <DisabledByDefaultSharpIcon className={classes.icon} />
                    <Typography className={classes.text}>Unterkategorie entfernen</Typography>
                  </Stack>
                </ButtonBase>
              ) : null}
            </Stack>
          </Stack>
        ))}
        <ButtonBase className={classes.addButton} onClick={addNewEffectSubCategory}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
            <AddBoxSharpIcon className={classes.icon} />
            <Typography className={classes.text}>weitere Unterkategorie</Typography>
          </Stack>
        </ButtonBase>
      </Stack>
    </BasicActionDialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    paddingBottom: '16px',
  },
  deleteButton: {
    color: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  addButton: {
    marginRight: 'auto',
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
  },
}));

export default AddNewEffectQuestionsDialog;
